import * as React from 'react';

import './Tooltip.scss';

export type TooltipPosition =
  | 'top'
  | 'top-start'
  | 'top-end'
  | 'right'
  | 'right-start'
  | 'right-end'
  | 'bottom'
  | 'bottom-start'
  | 'bottom-end'
  | 'left'
  | 'left-start'
  | 'left-end';

export interface ITooltipProps {
  id?: string;
  title: React.ReactNode;
  position?: TooltipPosition;
  className?: string;
}

function getIntValueFromPx(value: string): number {
  // get first digits from the string
  const matches = value.match(/\d+/g);
  if (!matches) {
    return 0;
  }
  // convert to number
  return +matches[0];
}

export function calculatePositionMatrix(
  position: TooltipPosition,
  hLeft: number,
  hTop: number,
  hWidth: number,
  hHeight: number,
  tWidth: number,
  tHeight: number
) {
  const matrix = {
    x: 0,
    y: 0,
    z: 0,
  };

  const isStart = position.endsWith('start');
  const isEnd = position.endsWith('end');

  const calculateX = (horizontalOffset: number) => {
    if (isStart) return hLeft + hWidth - tWidth;
    if (isEnd) return hLeft;
    return hLeft + horizontalOffset;
  };

  const calculateY = (verticalOffset: number) => {
    if (isStart) return hTop + hHeight - tHeight;
    if (isEnd) return hTop;
    return hTop + verticalOffset;
  };

  if (position.startsWith('top')) {
    matrix.y = hTop - tHeight;
    matrix.x = calculateX(0.5 * (hWidth - tWidth));
  } else if (position.startsWith('right')) {
    matrix.x = hLeft + hWidth;
    matrix.y = calculateY(0.5 * (hHeight - tHeight));
  } else if (position.startsWith('bottom')) {
    matrix.y = hTop + hHeight;
    matrix.x = calculateX(0.5 * (hWidth - tWidth));
  } else if (position.startsWith('left')) {
    matrix.x = hLeft - tWidth;
    matrix.y = calculateY(0.5 * (hHeight - tHeight));
  }

  return matrix;
}

export const Tooltip: React.FC<React.PropsWithChildren<ITooltipProps>> = ({
  children,
  className = '',
  id,
  position = 'bottom-start',
  title,
}) => {
  const [visible, setVisibility] = React.useState(false);
  const [animated, setAnimation] = React.useState(false);
  const handleRef = React.useRef<HTMLDivElement>(null);
  const tooltipRef = React.useRef<HTMLDivElement>(null);
  const timeout = React.useRef<any>();
  const calculateTransformation = () => {
    const noTransform = 'translate3d(0px, 0px, 0px)';
    const handle = handleRef.current;
    const tooltip = tooltipRef.current;
    if (!handle || !tooltip) {
      return noTransform;
    }
    const tStyles = window.getComputedStyle(tooltip);
    const hLeft = handle.offsetLeft;
    const hTop = handle.offsetTop;
    const hWidth = handle.offsetWidth;
    const hHeight = handle.offsetHeight;
    const tWidth = tooltip.offsetWidth + getIntValueFromPx(tStyles.marginLeft) + getIntValueFromPx(tStyles.marginRight);
    const tHeight =
      tooltip.offsetHeight + getIntValueFromPx(tStyles.marginTop) + getIntValueFromPx(tStyles.marginBottom);
    // skip if content not rendered yet
    if (tWidth <= 0) {
      return noTransform;
    }

    const matrix = calculatePositionMatrix(position, hLeft, hTop, hWidth, hHeight, tWidth, tHeight);

    return `translate3d(${matrix.x}px, ${matrix.y}px, ${matrix.z}px)`;
  };
  const show = () => {
    if (timeout.current) {
      clearTimeout(timeout.current);
      timeout.current = undefined;
    }
    setVisibility(true);
    setAnimation(false);
    timeout.current = setTimeout(() => setAnimation(true), 100);
  };
  const hide = () => {
    if (timeout.current) {
      clearTimeout(timeout.current);
      timeout.current = undefined;
    }
    timeout.current = setTimeout(() => {
      setVisibility(false);
      setAnimation(false);
    });
  };
  const handleClassName = `tooltip-handle ${className}`;
  const style: React.CSSProperties = {
    display: visible ? 'block' : 'none',
    opacity: animated ? '1' : '0',
    willChange: 'transform',
    transform: calculateTransformation(),
  };
  return (
    <div className="tooltip-container" onMouseEnter={show} onMouseLeave={hide}>
      <div id={id} ref={handleRef} className={handleClassName}>
        {children}
      </div>
      <div
        className="tooltip"
        role="tooltip"
        aria-hidden={!visible}
        x-placement={position}
        ref={tooltipRef}
        style={style}
        data-testid="tooltip">
        <div className="tooltip-arrow z-depth-2"></div>
        <div className="tooltip-inner z-depth-2">{title}</div>
      </div>
    </div>
  );
};

export default Tooltip;
